import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, ShoppingBag, Heart, X, Sparkles } from "lucide-react";
import { listGiftCardTemplates } from "../services/giftCards";
import { useCart } from "../contexts/CartContext";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";

export default function GiftCardsPage() {
  const { t, formatPrice, tp } = useApp();
  const { addGiftCard } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [tpls, setTpls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null);
  const [opts, setOpts] = useState({ recipient_name: "", recipient_email: "", gift_message: "" });

  useEffect(() => {
    listGiftCardTemplates({ activeOnly: true }).then((items) => {
      setTpls(items);
      setLoading(false);
    });
  }, []);

  const openModal = (tpl) => {
    setOpts({ recipient_name: "", recipient_email: "", gift_message: "" });
    setPicked(tpl);
  };

  const buy = () => {
    if (!picked) return;
    if (opts.recipient_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(opts.recipient_email)) {
      return alert("Düzgün e-poçt daxil edin və ya boş buraxın.");
    }
    addGiftCard(picked, opts);
    setPicked(null);
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16" data-testid="gift-cards-page">
      <div className="metadata">{tp("gift_card", "eyebrow", "VINYL BAKU · HƏDİYYƏ")}</div>
      <h1 className="section-title text-5xl md:text-7xl mt-3 flex items-center gap-4">
        <Gift size={48} className="text-[var(--primary)]" />
        {tp("gift_card", "title", "Hədiyyə Kartı")}
      </h1>
      <p className="text-[var(--text-2)] mt-5 max-w-3xl text-lg leading-relaxed">
        {tp("gift_card", "lede", "Sevdikləriniz üçün mükəmməl hədiyyə — istənilən vinili seçmə azadlığı. Hədiyyə kartını alın, müştəriyə avtomatik unikal endirim kodu çatdırılacaq və o, kataloqumuzdakı istənilən məhsulu öz seçimi ilə əldə edə bilər.")}
      </p>

      <div className="mt-12 flex items-center gap-3 metadata text-[var(--text-muted)]">
        <Sparkles size={14} className="text-[var(--accent)]" />
        <span>HƏR KARTIN UNİKAL KODU VAR · ÖZÜNÜZƏ VƏ YA SEVDİKLƏRİNİZƏ</span>
      </div>

      {loading ? (
        <div className="mt-16 text-center text-[var(--text-2)]">{t.common.loading}</div>
      ) : tpls.length === 0 ? (
        <div className="mt-16 border border-[var(--border)] p-16 text-center">
          <Gift size={36} className="text-[var(--text-muted)] mx-auto" />
          <div className="metadata mt-4 text-[var(--text-muted)]">HAZIRDA HEÇ BİR HƏDİYYƏ KARTI MÖVCUD DEYİL</div>
        </div>
      ) : (
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {tpls.map((tpl) => (
            <div
              key={tpl.id}
              className="border border-[var(--border)] hover:border-[var(--primary)] transition-colors group overflow-hidden"
              data-testid={`gift-tpl-card-${tpl.id}`}
            >
              <div className="aspect-[4/3] bg-gradient-to-br from-[var(--surface)] via-[var(--bg)] to-[var(--surface)] relative overflow-hidden flex items-center justify-center">
                {tpl.image_url ? (
                  <img src={tpl.image_url} alt={tpl.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <>
                    <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_30%_30%,var(--accent),transparent_55%)]" />
                    <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_70%_70%,var(--primary),transparent_55%)]" />
                    <div className="relative text-center">
                      <Gift size={48} className="mx-auto text-[var(--primary)]" />
                      <div className="font-display text-5xl mt-3">{Number(tpl.amount_azn || 0).toFixed(0)}</div>
                      <div className="metadata mt-1 text-[var(--text-2)]">AZN</div>
                    </div>
                  </>
                )}
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-2xl leading-tight">{tpl.name}</div>
                    <div className="metadata text-[var(--text-muted)] mt-1">
                      {tpl.multi_use ? "ÇOXLU İSTİFADƏ · QALIQ SAXLANILIR" : "TƏK İSTİFADƏ"}
                    </div>
                  </div>
                  <div className="font-mono text-[var(--primary)] text-lg whitespace-nowrap">
                    {formatPrice(Number(tpl.amount_azn) || 0)}
                  </div>
                </div>
                {tpl.description && (
                  <p className="text-sm text-[var(--text-2)] mt-3 line-clamp-3">{tpl.description}</p>
                )}
                <button
                  onClick={() => openModal(tpl)}
                  className="btn-primary w-full justify-center mt-5"
                  data-testid={`buy-gift-${tpl.id}`}
                >
                  <ShoppingBag size={14} /> SƏBƏTƏ AT
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* How it works */}
      <div className="mt-20 grid md:grid-cols-3 gap-8 border-t border-[var(--border-soft)] pt-12">
        <div>
          <div className="w-10 h-10 border border-[var(--primary)] text-[var(--primary)] flex items-center justify-center font-display text-lg">01</div>
          <div className="font-display text-2xl mt-4">Kartı seçin</div>
          <p className="text-[var(--text-2)] text-sm mt-2">Məbləğə uyğun şablonu seçib səbətə əlavə edin.</p>
        </div>
        <div>
          <div className="w-10 h-10 border border-[var(--primary)] text-[var(--primary)] flex items-center justify-center font-display text-lg">02</div>
          <div className="font-display text-2xl mt-4">Ödənişi tamamlayın</div>
          <p className="text-[var(--text-2)] text-sm mt-2">Adi sifariş kimi ödənişi keçirin — sevdiyiniz şəxsin adını da yaza bilərsiniz.</p>
        </div>
        <div>
          <div className="w-10 h-10 border border-[var(--primary)] text-[var(--primary)] flex items-center justify-center font-display text-lg">03</div>
          <div className="font-display text-2xl mt-4">Kod hazırdır</div>
          <p className="text-[var(--text-2)] text-sm mt-2">Sifariş təsdiqləndikdən sonra unikal endirim kodu profilinizdə görünəcək.</p>
        </div>
      </div>

      {!user && (
        <div className="mt-12 border border-[var(--border-soft)] p-6 bg-[var(--surface)] flex items-center gap-4">
          <Heart size={20} className="text-[var(--accent)]" />
          <div className="text-sm">
            <div className="font-display text-lg">Hesabınızla daha rahat</div>
            <div className="text-[var(--text-2)]">Aldığınız hədiyyə kartlarını profilinizdə saxlamaq üçün <button onClick={() => nav("/login?next=/gift-cards")} className="text-[var(--primary)] link-underline">giriş edin</button>.</div>
          </div>
        </div>
      )}

      {picked && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={() => setPicked(null)}>
          <div className="bg-[var(--bg)] border border-[var(--border)] w-full max-w-lg p-8" onClick={(e) => e.stopPropagation()} data-testid="gift-buy-modal">
            <div className="flex justify-between items-start">
              <div>
                <div className="metadata">HƏDİYYƏ MƏLUMATLARI</div>
                <h3 className="font-display text-3xl mt-1">{picked.name}</h3>
                <div className="font-mono text-[var(--primary)] text-xl mt-1">{formatPrice(Number(picked.amount_azn) || 0)}</div>
              </div>
              <button onClick={() => setPicked(null)}><X /></button>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="metadata">QƏBUL EDƏNİN ADI <span className="text-[var(--text-muted)] normal-case">— opsional, özünüz üçün boş buraxın</span></label>
                <input
                  className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                  placeholder="Ad Soyad"
                  value={opts.recipient_name}
                  onChange={(e) => setOpts({ ...opts, recipient_name: e.target.value })}
                  data-testid="gift-recipient-name"
                />
              </div>
              <div>
                <label className="metadata">QƏBUL EDƏNİN E-POÇTU <span className="text-[var(--text-muted)] normal-case">— hesabı olarsa, kart onun profilində də görünəcək</span></label>
                <input
                  type="email"
                  className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                  placeholder="ad@example.com"
                  value={opts.recipient_email}
                  onChange={(e) => setOpts({ ...opts, recipient_email: e.target.value })}
                  data-testid="gift-recipient-email"
                />
              </div>
              <div>
                <label className="metadata">MESAJ <span className="text-[var(--text-muted)] normal-case">— opsional</span></label>
                <textarea
                  rows={3}
                  maxLength={300}
                  className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                  placeholder="Doğum günün mübarək!"
                  value={opts.gift_message}
                  onChange={(e) => setOpts({ ...opts, gift_message: e.target.value })}
                  data-testid="gift-message"
                />
                <div className="metadata text-[var(--text-muted)] text-right mt-1">{opts.gift_message.length}/300</div>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button onClick={() => setPicked(null)} className="btn-outline flex-1 justify-center">LƏĞV ET</button>
              <button onClick={buy} className="btn-primary flex-1 justify-center" data-testid="gift-add-to-cart">
                <ShoppingBag size={14} /> SƏBƏTƏ AT
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
